import React, {Component} from 'react';
import {FaIcon} from "../FaIcon";
import {CampaignNotAssignedModal} from "../client/CampaignNotAssignedModal";
import { withTranslation } from 'react-i18next';

// Las fechas llegan formateadas del backend y traen "/" — sin esto i18next las escapa a entidades
// HTML y se ven crudas (gotcha de interpolación documentado en el CLAUDE.md del repo). Los nombres
// de cuenta pueden traer "&" o "/" por el mismo motivo.
const NO_ESCAPE = {escapeValue: false};

export class BusinessUnitAdAccountGeneralAlertIconClass extends Component {
    constructor(props) {
        super(props);

        this.campaignsNotAssignedUpdated = this.campaignsNotAssignedUpdated.bind(this);
    }

    render() {
        // Una sola pasada por las cuentas de la línea: `cssClassName`, `iconName` y el mensaje
        // agregado leen de acá. El tablero renderiza muchas filas y cada fila muchas cuentas.
        this._accountStatuses = this.groupAccountStatuses();

        const accountStatusMessage = this.accountStatusMessage();

        return (
            <div className={this.cssClassName()}>
                <FaIcon iconName={this.iconName()}/>
                {this.renderAlertTooltip(accountStatusMessage)}
            </div>
        )
    }

    hasAlert() {
        return this.props.hasCampaignsNotAssignedAlert || this.props.hasImpresionAlert || this.props.hasNoAccessAlert
    }

    // El reloj (ámbar) solo cuando el único problema son datos no actualizados: eso NUNCA es rojo.
    iconName() {
        return this.showsStaleState() ? "clock" : "exclamation-triangle";
    }

    renderAlertTooltip(accountStatusMessage) {
        const content = this.tooltipContent(accountStatusMessage, this.legacyContent());

        if (content) {
            // El `bu-tooltip` base es de una sola línea (nowrap): la lista de cuentas necesita
            // el modifier `-multiline`.
            const className = accountStatusMessage !== null ? "bu-tooltip -multiline" : "bu-tooltip";
            return <div className={className}>
                <FaIcon iconName={this.iconName()}/>
                <p className="tooltip-description">
                    {content}
                </p>
            </div>;
        }
    }

    // Mensajes que ya mostraba la celda (0 impresiones, campañas sin asignar, mensaje de la línea).
    legacyContent() {
        const content = this.hasAlert() ? this.alertMessage() : (this.hasInfo() ? this.infoMessage() : null);

        // `alertMessage` puede devolver un array vacío (el mensaje crudo de la línea queda suprimido
        // cuando ya mostramos el agregado por cuenta): un array vacío es truthy pero no es contenido.
        if (Array.isArray(content) && content.length === 0)
            return null;

        return content;
    }

    tooltipContent(accountStatusMessage, legacyContent) {
        if (accountStatusMessage === null)
            return legacyContent;

        if (!legacyContent)
            return accountStatusMessage;

        return <>{accountStatusMessage}<br/><br/>{legacyContent}</>;
    }

    hasInfo() {
        return this.props.hasCampaignsNotAssignedAlertSnoozed;
    }

    // Severidad a nivel LÍNEA: conexión (rojo) > alertas de la línea (rojo: 0 impresiones reales,
    // campañas sin asignar, mensaje de acceso legacy) > datos no actualizados (ámbar). Una cuenta
    // con datos viejos NUNCA degrada una alerta crítica de negocio que está activa.
    // (La prioridad conexión > stale > 0 impresiones del plan es la de UNA cuenta, no la de la línea.)
    cssClassName() {
        if (this.hasAccountsWithoutConnection() || this.hasAlert())
            return "bu-cell-icon no-impressions-alert"

        if (this.showsStaleState())
            return "bu-cell-icon data-stale"

        if (this.hasInfo())
            return "bu-cell-icon has-info"

        return "bu-cell-icon"
    }

    campaignsNotAssignedUpdated() {
        const { t } = this.props;
        alert(t('bucc_general_alert_tooltip.changes_saved_successfully'));
        this.props.doParentReReder();
    }

    infoMessage() {
        if (this.props.hasCampaignsNotAssignedAlertSnoozed) {
            return this.renderCampaignsNotAssignedMessage();
        }
    }

    renderCampaignsNotAssignedMessage() {
        const { t } = this.props;
        return (
            <span style={{display: "inline-flex", alignItems: "center"}}>
                    <span style={{marginRight: "0px"}}>{t('bucc_general_alert_tooltip.account_has_unassigned_campaigns')}</span>
                    <CampaignNotAssignedModal onFormSubmitted={this.campaignsNotAssignedUpdated}
                                              businessUnitId={this.props.businessUnitId}/>
                </span>);
    }

    alertMessage() {

        // Con estados por cuenta mostramos el agregado (`accountStatusMessage`) en vez del mensaje
        // crudo persistido por el backend, que colapsa los 3 estados en un texto ambiguo.
        if (this.props.hasNoAccessAlert && !this.hasAccountsWithoutConnection())
            return this.props.hasNoAccessAlert;

        let alertsComponents = [];

        if (this.props.hasImpresionAlert) {
            const { t } = this.props;
            alertsComponents.push(<span key="no-impressions">{t('bucc_general_alert_tooltip.account_has_no_impressions')}</span>);
        }

        if (this.props.hasCampaignsNotAssignedAlert) {
            if (alertsComponents.length > 0)
                alertsComponents.push(<br key="alerts-separator"/>);

            alertsComponents.push(
                React.cloneElement(this.renderCampaignsNotAssignedMessage(), {key: "campaigns-not-assigned"}));
        }

        return (alertsComponents);
    }

    // --- Estados por cuenta (`account_status` de cada fila de `adAccountsData`) ---
    // Compat: si las filas no traen `account_status` (backend viejo) todo esto queda en cero y la
    // celda se comporta exactamente como antes.

    accountStatusMessage() {
        const accountsWithoutConnection = this.accountsWithoutConnection();
        if (accountsWithoutConnection.length > 0)
            return this.accountsWithoutConnectionMessage(accountsWithoutConnection);

        const staleAccounts = this.staleAccounts();
        if (staleAccounts.length > 0)
            return this.accountsStaleMessage(staleAccounts);

        return null;
    }

    accountsWithoutConnectionMessage(affectedRows) {
        const { t } = this.props;
        const total = this.totalAccountsCount();
        const headline = total === 1
            ? t('bucc_general_alert_tooltip.account_without_connection_one', {
                name: this.accountName(affectedRows[0]),
                interpolation: NO_ESCAPE
            })
            : t('bucc_general_alert_tooltip.accounts_without_connection', {
                affected: affectedRows.length,
                total: total,
                interpolation: NO_ESCAPE
            });

        return this.accountStatusesBlock(headline, affectedRows, (row) => this.connectionStatusText(row));
    }

    accountsStaleMessage(affectedRows) {
        const { t } = this.props;
        const headline = t('bucc_general_alert_tooltip.accounts_stale', {
            affected: affectedRows.length,
            total: this.totalAccountsCount(),
            interpolation: NO_ESCAPE
        });

        return this.accountStatusesBlock(headline, affectedRows, (row) => this.staleStatusText(row));
    }

    // Mismo patrón visual que el tooltip de conversiones (`ConversionsAlertTooltip`): una cuenta
    // por bloque, nombre arriba y su estado abajo.
    accountStatusesBlock(headline, affectedRows, statusTextForRow) {
        // Sin `<br/>`: el layout lo hace el CSS con `display: block` (_tooltips.scss). Mezclar los dos
        // dejaba renglones vacíos entre cuentas y desalineaba el bloque.
        return (
            <span className="bu-tooltip-account-statuses">
                <span className="account-statuses-headline">{headline}</span>
                {affectedRows.map((row) => (
                    <span key={row.id} className="alert-tooltip-account-status">
                        <span className="account-name">{this.accountName(row)}</span>
                        <span className="account-status-value">{statusTextForRow(row)}</span>
                    </span>
                ))}
            </span>
        );
    }

    // El "por qué" de cada `error_kind` vive en el tooltip de la fila de la cuenta (mensajes largos
    // de `reconnect_modal.error_kind.*`). Acá, en el agregado de la línea, alcanza con el estado y
    // desde cuándo: nunca afirmamos "deshabilitada" a nivel línea.
    connectionStatusText(row) {
        const { t } = this.props;
        const noAccessSince = row.account_status.connection.no_access_since;

        if (!noAccessSince)
            return t('account_status.no_connection_no_date');

        return t('account_status.no_connection', {since: noAccessSince, interpolation: NO_ESCAPE});
    }

    staleStatusText(row) {
        const { t } = this.props;
        const lastUpdate = row.account_status.stale_since;

        if (!lastUpdate)
            return t('account_status.stale_title');

        return t('account_status.stale_detail', {last_update: lastUpdate, interpolation: NO_ESCAPE});
    }

    // Agrupa las cuentas de la línea por estado en UNA pasada. Se recalcula al entrar a `render()`
    // (no es estado: es una derivada de las props de este render).
    groupAccountStatuses() {
        const adAccountsData = Array.isArray(this.props.adAccountsData) ? this.props.adAccountsData : [];
        const withoutConnection = [];
        const stale = [];

        adAccountsData.forEach((row) => {
            if (!row || !row.account_status)
                return;

            if (row.account_status.connection)
                withoutConnection.push(row);
            else if (row.account_status.data_stale)
                stale.push(row);
        });

        return {total: adAccountsData.length, withoutConnection: withoutConnection, stale: stale};
    }

    accountStatuses() {
        if (this._accountStatuses === undefined)
            this._accountStatuses = this.groupAccountStatuses();

        return this._accountStatuses;
    }

    accountsWithoutConnection() {
        return this.accountStatuses().withoutConnection;
    }

    staleAccounts() {
        return this.accountStatuses().stale;
    }

    hasAccountsWithoutConnection() {
        return this.accountsWithoutConnection().length > 0;
    }

    // Stale es el estado DOMINANTE de la línea sólo si no hay nada más severo: ni cuentas sin
    // conexión, ni una alerta activa de la línea (0 impresiones reales / campañas sin asignar).
    showsStaleState() {
        return !this.hasAccountsWithoutConnection() && !this.hasAlert() && this.staleAccounts().length > 0;
    }

    totalAccountsCount() {
        return this.accountStatuses().total;
    }

    accountName(row) {
        return row.name ? row.name : row.account_id;
    }
}

export const BusinessUnitAdAccountGeneralAlertIcon = withTranslation()(BusinessUnitAdAccountGeneralAlertIconClass);
export default BusinessUnitAdAccountGeneralAlertIcon;
